<script lang="ts">
	import Logo from "@components/Logo.svelte";
	import PreloadedPrice from "@components/PreloadedPrice.svelte";
	import ProductDetailFathersFamilyBanner from "@components/ProductDetailFathersFamilyBanner.svelte";
	import ProductTags from "@components/ProductTags.svelte";
	import SubscriptionSideModal from "@components/SubscriptionSideModal.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Carousel from "@components/common/Carousel.svelte";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import Disclosure from "@components/common/Disclosure.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import { getMediaSourceFromProductGallery } from "@components/getMediaSourceFromProductGallery.js";
	import { getTagsForProductCard } from "@components/getTagsForProductCard.js";
	import Layout from "@components/layout/Layout.svelte";
	import ProductBasketButtons from "@components/product/ProductBasketButtons.svelte";
	import ProductStructuredData from "@components/product/ProductStructuredData.svelte";
	import { calculateDeliveryETA } from "@components/product/calculateDeliveryETA.js";
	import ProductSection from "@components/sections/ProductSection.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import info from "@core/assets/icons/info.svg?raw";
	import type { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { Locale } from "@core/schema/Locale.js";
	import type { ProductAttribute, ProductAttributeGroup, ProductVariantForCustomer } from "@core/schema/Product.js";
	import type { ProductGallery } from "@core/schema/ProductGallery.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { SubscriptionSelection } from "@core/schema/SubscriptionSelection.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { productAttributeGroupTypeNameMap } from "@core/schema/names/productAttributeGroupTypeNameMap.js";
	import { getMembershipPagePath } from "@core/schema/paths/getMembershipPagePath.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductAttributeGroupsOfValues } from "@core/schema/utils/getProductAttributeGroupsOfValues.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { formatPrice } from "@core/utils/formatPrice.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { reportProductDetail } from "../UniversalAnalytics.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { buildProductAlt } from "../seo/buildProductAlt.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../state/selectedDestinationCountry.svelte.js";
	import translations from "./ProductDetail.translations.json" with { type: "json" };
	import type { ProductDetailProps } from "./ProductDetailProps.js";

	const { product, subcategory, relatedProducts }: ProductDetailProps = $props();
	const { tenant, subcategories, cafeinaProduct, mothersBlendProduct } = getGlobalTemplateProps();

	const isCafeinaProduct = $derived(product.id === cafeinaProduct.id);
	const isMothersBlendProduct = $derived(product.id === mothersBlendProduct.id);
	const showSubscriptionOffer = $derived(
		(isCafeinaProduct || isMothersBlendProduct) && Auth.customerType === CustomerType.B2c,
	);

	const translate = getTranslate(translations);
	let subscriptionConfiguratorOpen = $state(false);

	const [defaultVariant] = $derived(product.variants);
	const defaultAttributes = $derived.by(() => {
		const grouped: Record<ProductAttributeGroup["id"], ProductAttribute["id"]> = {};
		for (const group of product.attributeGroups) {
			const [first] = group.values;
			if (!first) {
				continue;
			}
			grouped[group.id] = first.id;
		}
		return grouped;
	});

	let descriptionExpanded = $state(false);
	let selectedVariant = $derived(defaultVariant?.id);
	let selectedAttributes = $derived(defaultAttributes);
	const deliveryDate = $derived(calculateDeliveryETA(new Date(), selectedDestinationCountry.current));
	const descriptionExpandAllowed = $derived(
		product.shortDescription[tenant.locale] !== product.description[tenant.locale],
	);

	const productVariant = $derived(
		selectedVariant !== undefined ? getProductOfVariant(product, selectedVariant) : undefined,
	);
	const productAttributes = $derived(getProductAttributeGroupsOfValues(product.attributeGroups, selectedAttributes));

	onMount(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const variant = searchParams.get("variant");
		if (variant !== null) {
			selectedVariant = variant;
		}
		for (const group of product.attributeGroups) {
			const value = searchParams.get(`attribute-${group.id}`);
			if (value !== null) {
				selectedAttributes = {
					...selectedAttributes,
					[group.id]: value,
				};
			}
		}
		if (!selectedVariant) {
			throw new Error(`No variant selected for product: ${product.id}`);
		}
	});

	let reported = $state(false);
	$effect(() => {
		if (
			reported ||
			selectedCurrency.current === null ||
			selectedDestinationCountry.current === null ||
			!selectedVariant
		) {
			return;
		}
		const productVariant = getProductOfVariant(product, selectedVariant);
		const productAttributes = getProductAttributeGroupsOfValues(product.attributeGroups, selectedAttributes);
		reportProductDetail(tenant.locale, productVariant, productAttributes, {
			currency: selectedCurrency.current,
			destinationCountry: selectedDestinationCountry.current,
			subcategories,
			vatType: EconomicSubjectVatType.NotVatSubject,
		});
		reported = true;
	});

	function selectVariant(id: ProductVariantForCustomer["id"]): void {
		selectedVariant = id;
		const url = new URL(window.location.href);
		if (defaultVariant?.id === id) {
			url.searchParams.delete("variant");
		} else {
			url.searchParams.set("variant", id);
		}
		window.history.replaceState({}, "", url);
	}

	function selectAttribute(group: ProductAttributeGroup, value: ProductAttribute): void {
		selectedAttributes = {
			...selectedAttributes,
			[group.id]: value.id,
		};
		const url = new URL(window.location.href);
		if (defaultAttributes[group.id] === value.id) {
			url.searchParams.delete(`attribute-${group.id}`);
		} else {
			url.searchParams.set(`attribute-${group.id}`, value.id);
		}
		window.history.replaceState({}, "", url);
	}

	const selectedCombinationStock = $derived.by(() => {
		const variant = productVariant?.variant ?? defaultVariant;
		if (variant === undefined) {
			return Infinity;
		}

		return getProductStockAmount(product.stock, variant, productAttributes);
	});

	const primaryMedia = $derived.by(() => {
		if (!productVariant) {
			return undefined;
		}

		return getProductPrimaryMedia(productVariant, productAttributes);
	});

	const gallery = $derived.by((): ProductGallery[] => {
		if (!primaryMedia) {
			return product.gallery;
		}

		return [
			{
				id: crypto.randomUUID(),
				photo: primaryMedia,
			},
			...product.gallery,
		];
	});

	const piecesInflect = {
		[Locale.cs]: makeInflect("kus", "kusy", "kusů"),
		[Locale.en]: makeInflect("piece", "pieces", "pieces"),
	}[tenant.locale];

	const galleryFallbackAlt = $derived(
		buildProductAlt(product, subcategory, tenant.locale, productAttributes, productVariant?.variant),
	);
</script>

<ProductStructuredData {product} {subcategory} />

<Layout pageHasMobileBottomBar ogImage={primaryMedia}>
	<div class="grid grid-cols-12 lg:gap-16 col-span-full mb-10">
		<div class="col-span-full xl:col-span-5 flex flex-col gap-4 lg:gap-6">
			<Heading
				level={1}
				text={product.name[tenant.locale]}
				class="font-antipol text-8/tighter lg:text-10 font-medium"
			/>
			<div class="text-base/normal !text-fcrgray">
				<CkEditorContent
					html={descriptionExpanded ? product.description[tenant.locale] : product.shortDescription[tenant.locale]}
					class="text-sm/normal sm:text-base"
				/>

				{#if descriptionExpandAllowed}
					<button
						type="button"
						class="underline inline-block mt-4"
						onclick={(): void => {
							descriptionExpanded = !descriptionExpanded;
						}}
					>
						<Formatted text={translate(descriptionExpanded ? "showLess" : "readMore")} />
					</button>
				{/if}
			</div>
			{#if product.variantType !== ProductVariantType.None}
				<div class="flex items-center gap-2 overflow-x-auto text-nowrap p-4 -my-4">
					{#each product.variants as variant (variant.id)}
						<Button
							type="button"
							variant={ButtonVariant.Outlined}
							size={ButtonSize.Medium}
							class={[
								selectedVariant === variant.id &&
									"outline-2 outline-fcrdarkgray !cursor-default outline [&:not(:disabled):hover]:border-transparent",
								product.variants.length > 2 && "xl:w-full",
							]}
							onclick={(): void => {
								selectVariant(variant.id);
							}}
						>
							{variant.label[tenant.locale]}
						</Button>
					{/each}
				</div>
			{/if}
			{#each product.attributeGroups as group (group.id)}
				<div class="flex flex-col gap-2">
					<div class="text-fcrdarkgray text-5/tighter font-medium font-antipol">
						<Formatted text={productAttributeGroupTypeNameMap[group.type][tenant.locale]} />:
					</div>
					<!-- Enable this if when adding more attribute group types. Right now linter doesn't like it because the enum has only one value -->
					<!-- {#if attributeGroups.type === ProductAttributeGroupType.Color} -->
					<div class="flex items-center gap-2">
						{#each group.values as value (value.id)}
							<div
								class={[
									"size-9 rounded-full flex items-center justify-center",
									selectedAttributes[group.id] === value.id
										? "outline outline-fcrdarkgray"
										: "hover:outline hover:outline-fcrlightgray",
								]}
							>
								<button
									class={["size-8 rounded-full border border-fcrgray/30"]}
									style={`background-color: ${value.value}`}
									title={value.label[tenant.locale]}
									onclick={(): void => {
										selectAttribute(group, value);
									}}
								></button>
							</div>
						{/each}
					</div>
					<!-- {/if} -->
				</div>
			{/each}
			{#if productVariant}
				<PreloadedPrice class="h-12">
					{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
						{@const { withTax, withoutTax, taxRate } = getPriceForCustomerOfProductVariant(
							productVariant,
							currency,
							destinationCountry,
							EconomicSubjectVatType.NotVatSubject,
						)}
						<div class="flex items-start flex-col xl:flex-row xl:items-end gap-x-2 gap-y-1 mt-2 lg:mt-0">
							<span class="font-antipol font-medium text-6 lg:text-10 leading-tighter">
								{#if currency === Currency.CZK}
									<Formatted text={formatPrice(Math.ceil(withTax), currency, tenant.locale, false)} />
								{:else}
									<Formatted text={formatPrice(withTax, currency, tenant.locale, true)} />
								{/if}
							</span>
							<span class="text-fcrgray text-3 xl:text-4 leading-normal xl:mb-1">
								{#if taxRate !== 0}
									<Formatted text={formatPrice(withoutTax, currency, tenant.locale, true)} />
								{/if}
								<Formatted text={translate("withoutTax")} />
							</span>
						</div>
					{/snippet}
				</PreloadedPrice>

				<ProductBasketButtons
					maxQuantity={selectedCombinationStock}
					product={productVariant}
					attributes={productAttributes}
					class="fixed xl:static bottom-0 inset-x-0 bg-white z-10 p-4 xl:p-0"
				/>
			{/if}

			<div class="flex flex-col gap-3">
				<div class="text-fcrgray text-4 flex items-center gap-2">
					{#if selectedCombinationStock > 0}
						<div class="size-4 bg-fcrsuccess rounded-full"></div>
						<div>
							{#if deliveryDate}
								<Formatted
									text={translate("deliveredUntil", {
										deliveryDate: toDateMonthYearFormat(deliveryDate, tenant.locale),
									})}
								/>
							{:else}
								<Formatted text={translate("inStock")} />
							{/if}
						</div>
					{:else}
						<div class="size-4 bg-fcrerror rounded-full"></div>
						<div>
							<Formatted text={translate("soldOut")} />
						</div>
					{/if}
				</div>
				{#if product.amountInOnePackage !== undefined}
					<div class="text-fcrgray text-4 flex items-center gap-2">
						<Icon icon={info} class="size-4" />
						<div>
							<Formatted
								text={translate("amountInOnePackage", {
									amount: product.amountInOnePackage,
									unit: piecesInflect(product.amountInOnePackage),
								})}
							/>
						</div>
					</div>
				{/if}
			</div>

			<div class="xl:hidden col-span-full">
				{#if gallery.length > 0}
					<Carousel items={gallery} options={{ loop: true, align: "start" }}>
						{#snippet item(media: ProductGallery, imageIndex: number, dupe: boolean)}
							<div class="h-70 relative px-1">
								<Media
									eager={imageIndex === 0}
									sourcesets={media.photo ? { ">xl": [0, 800], "<xl": [500, 0] } : {}}
									source={getMediaSourceFromProductGallery(media)}
									alt={media.photo?.title ?? galleryFallbackAlt}
									width={400}
									height={225}
									class="size-full object-cover"
									lightbox={!dupe}
									lightboxWidth={1280}
								/>
								{#if imageIndex === 0}
									<ProductTags
										class="absolute top-4 left-4"
										tags={getTagsForProductCard({ product, subcategory, locale: tenant.locale })}
									/>
								{/if}
							</div>
						{/snippet}
					</Carousel>
				{:else}
					<div class="h-50 px-1 bg-fcrlightgray-300 rounded">
						<Logo class="w-full h-full p-5" />
					</div>
				{/if}
			</div>
			{#if !Auth.loggedCustomer}
				<ProductDetailFathersFamilyBanner
					class="bg-fcrbrown"
					heading={translate("betterOfferWithMembership")}
					label={translate("betterOfferWithMembershipDescription")}
					action={{ href: getMembershipPagePath(tenant) }}
				/>
			{:else if showSubscriptionOffer}
				<ProductDetailFathersFamilyBanner
					class="bg-fcrblue"
					heading={translate("betterOfferWithSubscription")}
					label={translate("betterOfferWithSubscriptionDescription")}
					action={(): void => {
						const cart = Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery);
						cart.subscriptionModalStep = 0;
						Cart.saveSubscriptionFields({
							selection: isCafeinaProduct ? SubscriptionSelection.Cafeina : SubscriptionSelection.MothersBlend,
							variant: SubscriptionVariant.Espresso,
						});
						subscriptionConfiguratorOpen = true;
					}}
				/>
			{/if}
			<div>
				{#each product.disclosures[tenant.locale] as disclosure, i (i)}
					<Disclosure title={disclosure.title}>
						<CkEditorContent html={disclosure.content} class="text-sm/normal sm:text-base !text-fcrgray" />
					</Disclosure>
				{/each}
			</div>
		</div>
		<div class="hidden order-first xl:flex flex-col gap-2 col-span-7">
			{#each gallery as media, index (media.id)}
				<div class="relative">
					<Media
						lightbox
						sourcesets={media.photo ? { ">xl": [0, 800], "<xl": [200, 0] } : {}}
						source={media.photo ?? { youtube: media.youtube ?? "" }}
						alt={media.photo?.title ?? galleryFallbackAlt}
						width={400}
						height={225}
						class="w-full"
					/>
					{#if index === 0}
						<ProductTags
							class="absolute top-4 left-4"
							tags={getTagsForProductCard({ product, subcategory, locale: tenant.locale })}
						/>
					{/if}
				</div>
			{:else}
				<div class="w-full relative pb-[56.25%]">
					<div class="absolute inset-0 p-20 bg-fcrlightgray-300 rounded">
						<Logo class="w-full h-full" />
					</div>
				</div>
			{/each}
		</div>
	</div>
	<ProductSection {subcategories} products={relatedProducts} title={translate("relatedProducts")} class="bg-fcrbeige" />
	{#if showSubscriptionOffer}
		<SubscriptionSideModal bind:show={subscriptionConfiguratorOpen} />
	{/if}
</Layout>
